import { useState } from "react";
import { ChevronDown, ChevronRight, Sparkles, Loader2 } from "lucide-react";
import api from "../../../lib/api";

/**
 * Brief 3.13 follow-up (Feb 25 2026) — Claude bundle insight expander.
 *
 * Surfaces the `notes` field from `nerea_bundle_classifications` so AMs
 * can see Claude's commentary about the split (e.g. "Page 8 is a shared
 * signature page included in both ranges"). Lazy-loaded — fetches only
 * when the AM clicks to expand.
 *
 * Compact by design: blends into the bundle row, doesn't add a new
 * column or take vertical space until opened.
 */
export default function BundleInsight({ merchantId, fileHash }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const onToggle = async () => {
    if (!open && !data) {
      setLoading(true); setErr(null);
      try {
        const { data: body } = await api.get(
          `/admin/merchants/${merchantId}/bundle-insights/${fileHash}`,
        );
        setData(body);
      } catch (e) {
        setErr(e?.response?.status === 404
          ? "Claude hasn't classified this bundle yet — refresh in ~10s."
          : "Couldn't load insight.");
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  return (
    <span className="inline-flex items-center" data-testid={`bundle-insight-${fileHash?.slice(0, 8)}`}>
      <button
        type="button"
        onClick={onToggle}
        className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-cyan-300/70 hover:text-cyan-200 transition-colors"
        title="Claude's commentary about how this bundle was split"
      >
        <Sparkles className="h-2.5 w-2.5" />
        Claude insight
        {open ? <ChevronDown className="h-2.5 w-2.5" /> : <ChevronRight className="h-2.5 w-2.5" />}
      </button>
      {open && (
        <div
          className="ml-2 inline-block max-w-md rounded-lg border border-cyan-400/20 bg-cyan-400/[0.04] px-3 py-2"
          data-testid="bundle-insight-content"
        >
          {loading ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </span>
          ) : err ? (
            <span className="text-[11px] text-zinc-500">{err}</span>
          ) : data ? (
            <>
              {data.notes && (
                <div className="text-[11.5px] text-zinc-200 leading-relaxed whitespace-pre-wrap mb-1.5">
                  {data.notes}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                Classified {new Date(data.created_at).toLocaleString()}
                {data.total_pages ? ` · ${data.total_pages} pages total` : ""}
              </div>
            </>
          ) : null}
        </div>
      )}
    </span>
  );
}
